import { IGetRecordResponse } from '@principle-theorem/practice-migrations/interfaces';
import {
  PatientStatus,
  type IPatient,
} from '@principle-theorem/principle-core/interfaces';
import {
  ISO_DATE_FORMAT,
  Region,
  WithRef,
  toISODate,
} from '@principle-theorem/shared';
import * as he from 'he';
import * as moment from 'moment-timezone';
import {
  BaseExistingPatientDestinationEntity,
  type IExistingPatientMergeConflictSummary,
} from '../../../destination/entities/existing-patient';
import {
  CorePracticePatient,
  PatientSourceEntity,
} from '../../source/entities/patients';
import { getGender, getName, getPhone } from './patients';

export class ExistingPatientDestinationEntity extends BaseExistingPatientDestinationEntity<CorePracticePatient> {
  patientSourceEntity = new PatientSourceEntity();

  override sourceEntities = {
    patients: this.patientSourceEntity,
  };

  getDateOfBirth(
    sourcePatient: IGetRecordResponse<CorePracticePatient>
  ): string | undefined {
    return toISODateOfBirth(sourcePatient.data.dateOfBirth);
  }

  filterMatchingPatients(
    sourcePatient: IGetRecordResponse<CorePracticePatient>,
    matchingPatients: WithRef<IPatient>[]
  ): WithRef<IPatient>[] {
    const sourceNames = [
      sourcePatient.data.firstName,
      sourcePatient.data.lastName,
    ].map((name) => he.decode(name).trim().toLowerCase());

    return matchingPatients.filter(
      (patient) =>
        sourceNames.every((name) =>
          patient.name.toLowerCase().includes(name)
        ) && patient.status === PatientStatus.Active
    );
  }

  buildConflictSummary(
    sourcePatient: CorePracticePatient,
    region: Region
  ): Omit<IExistingPatientMergeConflictSummary, 'status' | 'patientRefs'> {
    const sourcePatientId = this.patientSourceEntity
      .getSourceRecordId(sourcePatient.data)
      .toString();

    return {
      sourcePatientId,
      patientName: getName(sourcePatient.data),
      dateOfBirth: toISODateOfBirth(sourcePatient.data.dateOfBirth) ?? '',
      gender: getGender(sourcePatient.data),
      phone: getPhone(sourcePatient.data, region),
      email: sourcePatient.data.email ?? '',
    };
  }
}

function toISODateOfBirth(dateOfBirth: string): string | undefined {
  const parsed = moment.utc(dateOfBirth, ISO_DATE_FORMAT);
  return dateOfBirth && parsed.isValid() ? toISODate(parsed) : undefined;
}
